/**
 * Chapter progression data for BuildBadge.
 * 5 Arcs × 10 unique chapter names = 10 chapters per arc, cycling 20 times to fill 200 entries.
 * Each chapter gets V1, V2, V3 before moving to the next.
 */

export const ARCS = [
  "Débuts chaotiques",
  "Premiers progrès",
  "Rechute & doutes",
  "Try Harder",
  "Champion & Gloire",
] as const;

export const CHAPTER_NAMES = [
  "silencieux réveil",
  "brutal chute",
  "incertain défi",
  "explosif renaissance",
  "fragile retour",
  "naissant épreuve",
  "improbable rupture",
  "acharné combat",
  "solitaire espoir",
  "déterminé élan",
] as const;

/** Total unique chapters: 5 arcs × 200 entries = 1000 */
const CHAPTERS_PER_ARC = 200;
const TOTAL_CHAPTERS = ARCS.length * CHAPTERS_PER_ARC;
const VERSIONS_PER_CHAPTER = 9;

export interface ChapterInfo {
  arc: string;
  chapter: string;
  version: number; // 1, 2, or 3
  chapterNumber: number; // 1-based global chapter number
}

/**
 * Given a sequential counter (0-based), return the current chapter info.
 * Cycles through all 1000 chapters × 3 versions = 3000 total steps.
 */
export function getChapterFromCounter(counter: number): ChapterInfo {
  const totalSteps = TOTAL_CHAPTERS * VERSIONS_PER_CHAPTER;
  const safeCounter = ((counter % totalSteps) + totalSteps) % totalSteps;

  const chapterIndex = Math.floor(safeCounter / VERSIONS_PER_CHAPTER);
  const version = (safeCounter % VERSIONS_PER_CHAPTER) + 1;

  const arcIndex = Math.floor(chapterIndex / CHAPTERS_PER_ARC);
  const chapterInArc = chapterIndex % CHAPTERS_PER_ARC;
  const chapterNameIndex = chapterInArc % CHAPTER_NAMES.length;

  return {
    arc: ARCS[arcIndex],
    chapter: CHAPTER_NAMES[chapterNameIndex],
    version,
    chapterNumber: chapterIndex + 1,
  };
}

const COUNTER_KEY = "perform_chapter_counter";

/**
 * Get current counter from localStorage, increment on new build.
 */
export function getAndAdvanceChapter(buildId: string): ChapterInfo {
  const stored = localStorage.getItem(COUNTER_KEY);
  let data: { buildId: string; counter: number } | null = null;

  try {
    data = stored ? JSON.parse(stored) : null;
  } catch {
    data = null;
  }

  if (!data) {
    // First time: start at 0
    const counter = 0;
    localStorage.setItem(COUNTER_KEY, JSON.stringify({ buildId, counter }));
    return getChapterFromCounter(counter);
  }

  if (data.buildId !== buildId) {
    // New build detected: advance counter
    const counter = data.counter + 1;
    localStorage.setItem(COUNTER_KEY, JSON.stringify({ buildId, counter }));
    return getChapterFromCounter(counter);
  }

  // Same build: return current
  return getChapterFromCounter(data.counter);
}
